
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, BarChart, Bar } from 'recharts';
import { Boxes, AlertTriangle, Activity, DollarSign } from "lucide-react";
import { useEffect } from "react";

export const InventoryStats = ({ periodId }: { periodId: string }) => {
  const { data: stats, refetch } = useQuery({
    queryKey: ['inventory-stats', periodId],
    queryFn: async () => {
      // Optimisation: Récupération de toutes les données en une seule requête
      const { data: products, error } = await supabase
        .from('inventory_products')
        .select(`
          *,
          inventory_movements(
            current_stock,
            movement_date,
            movement_type
          ),
          category:product_categories(
            name
          )
        `)
        .eq('period_id', periodId)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching inventory stats:', error);
        throw error;
      }

      if (!products || products.length === 0) return {
        totalProducts: 0,
        lowStock: 0,
        totalMovements: 0,
        chartData: [],
        totalValue: 0,
        averageValue: 0,
        stockByCategory: [],
      };

      // Calcul précis des statistiques
      const productsWithStock = products.map(p => {
        const latestMovement = p.inventory_movements?.[0];
        return {
          ...p,
          current_stock: latestMovement?.current_stock || 0
        };
      });

      const totalProducts = productsWithStock.length;
      const lowStock = productsWithStock.filter(p => 
        p.minimum_threshold && p.current_stock <= p.minimum_threshold
      ).length;

      // Agrégation des mouvements par date
      const movementsByDate = products.reduce((acc: Record<string, number>, product) => {
        product.inventory_movements?.forEach(movement => {
          const date = new Date(movement.movement_date).toLocaleDateString();
          acc[date] = (acc[date] || 0) + 1;
        });
        return acc;
      }, {});

      const chartData = Object.entries(movementsByDate)
        .map(([date, count]) => ({
          date,
          movements: count,
        }))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      // Calculs financiers précis
      const totalValue = productsWithStock.reduce((sum, product) => 
        sum + (product.current_stock * (product.unit_price || 0)), 0
      );

      const averageValue = totalProducts > 0 
        ? totalValue / totalProducts 
        : 0;

      const totalMovements = products.reduce((sum, product) => 
        sum + (product.inventory_movements?.length || 0), 0
      );

      return {
        totalProducts,
        lowStock,
        totalMovements,
        chartData,
        totalValue,
        averageValue,
        stockByCategory: productsWithStock.reduce((acc: any[], product) => {
          const categoryName = product.category?.name || 'Non catégorisé';
          const value = product.current_stock * (product.unit_price || 0);
          
          const existingCategory = acc.find(c => c.name === categoryName);
          if (existingCategory) {
            existingCategory.value += value;
          } else {
            acc.push({ name: categoryName, value });
          }
          
          return acc;
        }, []),
      };
    },
  });

  // Réaltime subscription
  useEffect(() => {
    const channel = supabase
      .channel('inventory-changes')
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'inventory_movements',
        filter: `period_id=eq.${periodId}`,
      }, () => {
        console.log('Inventory movement detected, refetching stats...');
        refetch();
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'inventory_products',
        filter: `period_id=eq.${periodId}`,
      }, () => {
        console.log('Product update detected, refetching stats...');
        refetch();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [periodId, refetch]);

  if (!stats) return null;

  return (
    <div className="space-y-3">
      {/* Grid des statistiques avec espacement réduit */}
      <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4">
        {/* Carte Total Produits avec taille réduite */}
        <Card className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 transition-all duration-300 hover:scale-102 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between p-3">
            <CardTitle className="text-sm font-medium">Total Produits</CardTitle>
            <Boxes className="h-4 w-4 text-blue-500" />
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="text-lg font-bold flex items-baseline gap-2">
              {stats.totalProducts}
              <span className="text-xs font-normal text-muted-foreground">produits</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Valeur moyenne: 
              <span className="font-semibold ml-1">
                {stats.averageValue?.toFixed(2) || '0.00'} €/produit
              </span>
            </p>
          </CardContent>
        </Card>

        {/* Carte Stock Faible avec taille réduite */}
        <Card className="bg-gradient-to-br from-red-500/10 to-orange-500/10 transition-all duration-300 hover:scale-102 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between p-3">
            <CardTitle className="text-sm font-medium">Stock Faible</CardTitle>
            <AlertTriangle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="text-lg font-bold flex items-baseline gap-2">
              {stats.lowStock}
              <span className="text-xs font-normal text-muted-foreground">produits</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              <span className="font-semibold">
                {((stats.lowStock / (stats.totalProducts || 1)) * 100).toFixed(1)}%
              </span> du stock total
            </p>
          </CardContent>
        </Card>

        {/* Carte Mouvements avec taille réduite */}
        <Card className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 transition-all duration-300 hover:scale-102 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between p-3">
            <CardTitle className="text-sm font-medium">Mouvements</CardTitle>
            <Activity className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent className="p-3 pt-0">
            <div className="text-lg font-bold flex items-baseline gap-2">
              {stats.totalMovements}
              <span className="text-xs font-normal text-muted-foreground">opérations</span>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Valeur totale: 
              <span className="font-semibold ml-1">
                {stats.totalValue?.toFixed(2) || '0.00'} €
              </span>
            </p>
          </CardContent>
        </Card>

        {/* Carte Tendance avec taille réduite */}
        <Card className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 transition-all duration-300 hover:scale-102 hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between p-3">
            <CardTitle className="text-sm font-medium">Tendance</CardTitle>
            <DollarSign className="h-4 w-4 text-purple-500" />
          </CardHeader>
          <CardContent className="p-3 pt-0 h-[60px]">
            <ResponsiveContainer width="100%" height={40}>
              <LineChart data={stats.chartData}>
                <Line 
                  type="monotone" 
                  dataKey="movements" 
                  stroke="#8b5cf6" 
                  strokeWidth={2} 
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Graphique des mouvements avec taille réduite */}
      <Card className="p-3">
        <CardHeader className="p-3 pb-0">
          <CardTitle className="text-base">Mouvements de Stock</CardTitle>
        </CardHeader>
        <CardContent className="p-3">
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={stats.chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                tick={{ fill: '#6b7280', fontSize: 11 }}
                height={35}
              />
              <YAxis 
                tick={{ fill: '#6b7280', fontSize: 11 }}
                label={{ 
                  value: 'Opérations', 
                  angle: -90, 
                  position: 'insideLeft',
                  fill: '#6b7280',
                  fontSize: 11
                }}
                width={35}
              />
              <Tooltip 
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  padding: '0.5rem',
                  fontSize: '11px'
                }}
                formatter={(value) => [`${value} opérations`, 'Mouvements']}
              />
              <Bar 
                dataKey="movements" 
                fill="#8b5cf6" 
                radius={[4, 4, 0, 0]}
                barSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </div>
  );
};

